import { motion } from "framer-motion";
import { useState } from "react";

/**
 * EnvelopeLoader
 * -----------------------------------------------------------
 * Full-viewport landing scene. A single centered stack: blessing,
 * names, envelope and CTA.
 *
 * Click the envelope (or the CTA button) → Framer Motion plays
 * the open animation (flap rotates, seal breaks, envelope scales)
 * → onOpen() fires once the motion is done.
 */
const PETAL_STYLES = [
  { left: "6%", delay: 0, duration: 17, size: 14, rot: 20, color: "#E91E63" },
  { left: "24%", delay: 3, duration: 20, size: 11, rot: -35, color: "#E05A3F" },
  { left: "44%", delay: 1, duration: 21, size: 16, rot: 60, color: "#FFB300" },
  { left: "66%", delay: 5, duration: 18, size: 12, rot: -10, color: "#E91E63" },
  { left: "86%", delay: 2, duration: 22, size: 13, rot: 45, color: "#E91E63" },
];

const ITEM = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function EnvelopeLoader({ onOpen }) {
  const [opening, setOpening] = useState(false);

  const handleOpen = () => {
    if (opening) return;
    setOpening(true);
    // Total timeline ~= 0.9s flap + 0.7s fade. Fire callback a bit before the
    // fade ends so the next scene can start mounting.
    setTimeout(() => onOpen?.(), 1700);
  };

  return (
    <section
      className="relative w-full h-full overflow-hidden flex items-center justify-center text-ivory"
      style={{
        background:
          "radial-gradient(ellipse at 30% 40%, rgba(184,23,74,0.18) 0%, transparent 55%)," +
          "radial-gradient(ellipse at 70% 70%, rgba(201,169,97,0.12) 0%, transparent 60%)," +
          "linear-gradient(180deg, #4A0E22 0%, #2A0814 100%)",
      }}
    >
      {/* Mandala corners */}
      <CornerOrnament className="top-4 left-4" />
      <CornerOrnament className="top-4 right-4" flipX />
      <CornerOrnament className="bottom-4 left-4 [@media(max-height:640px)]:hidden" flipY />
      <CornerOrnament className="bottom-4 right-4 [@media(max-height:640px)]:hidden" flipX flipY />

      {/* Floating petals */}
      {PETAL_STYLES.map((p, i) => (
        <svg
          key={i}
          viewBox="0 0 20 28"
          className="absolute animate-petal-fall pointer-events-none"
          style={{
            left: p.left,
            top: 0,
            width: `${p.size}px`,
            transform: `rotate(${p.rot}deg)`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
          aria-hidden
        >
          <path d="M10 0 C18 8 18 20 10 28 C2 20 2 8 10 0Z" fill={p.color} fillOpacity="0.7" />
        </svg>
      ))}

      {/* Main stack */}
      <div className="relative z-10 w-full h-full px-6 flex flex-col items-center justify-center text-center">
        <motion.div
          className="relative z-20 flex flex-col items-center"
          animate={opening ? { opacity: 0, y: -20 } : { opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.65, 0, 0.35, 1] }}
        >
          <motion.div
            className="flex flex-col items-center"
            initial="hidden"
            animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.15 } } }}
          >
            <motion.img
              src="/ornaments/ganesh.png"
              alt="Shri Ganesh"
              className="w-auto"
              style={{
                height: "clamp(48px, 8vh, 80px)",
                filter: "drop-shadow(0 0 14px rgba(232,204,126,0.45))",
              }}
              variants={ITEM}
            />
            <motion.div
              variants={ITEM}
              className="font-hindi text-gold-light mt-2"
              style={{ fontSize: "clamp(18px, 2.6vh, 24px)" }}
            >
              ॥ श्री गणेशाय नमः ॥
            </motion.div>
            <motion.p
              variants={ITEM}
              className="font-hindi not-italic text-gold-light/80 leading-relaxed mt-1"
              style={{ fontSize: "clamp(14px, 2.1vh, 19px)" }}
            >
              ॥ वक्रतुण्ड महाकाय सूर्यकोटि समप्रभ।
              <br />
              निर्विघ्नं कुरु मे देव सर्वकार्येषु सर्वदा ॥
            </motion.p>
            <motion.div
              variants={ITEM}
              className="flex items-center gap-3"
              style={{ margin: "clamp(10px, 2vh, 20px) 0" }}
              aria-hidden
            >
              <span className="h-px w-12 bg-gold/50" />
              <span className="text-gold text-[8px] leading-none">◆</span>
              <span className="h-px w-12 bg-gold/50" />
            </motion.div>
            <motion.p
              variants={ITEM}
              className="font-body text-[10px] sm:text-[11px] uppercase tracking-[0.35em] text-ivory/70"
            >
              Your invitation to the wedding of
            </motion.p>
            <motion.h1
              variants={ITEM}
              className="font-display italic font-light leading-none text-ivory tracking-tight mt-3 text-[clamp(36px,11vw,48px)] sm:text-[clamp(34px,6vh,64px)]"
            >
              Sonakshi
              <br className="sm:hidden" />
              <span className="text-gold-light italic px-2">&amp;</span>
              <em className="italic">Shubham</em>
            </motion.h1>
          </motion.div>
        </motion.div>

        {/* Envelope stage */}
        <div
          className="relative [perspective:1600px]"
          style={{
            width: "min(460px, 82vw, calc(30vh * 1.55))",
            marginTop: "clamp(14px, 3vh, 32px)",
          }}
        >
          <div
            className="absolute -inset-10 pointer-events-none"
            style={{ background: "radial-gradient(ellipse, rgba(232,204,126,0.22) 0%, transparent 65%)" }}
          />
          <motion.div
            onClick={handleOpen}
            className="relative cursor-pointer w-full aspect-[1.55/1] [transform-style:preserve-3d]"
            whileHover={{ y: -6, rotate: -0.8 }}
            animate={
              opening
                ? { scale: 1.35, y: -40 }
                : { scale: 1, y: 0 }
            }
            transition={{ duration: opening ? 1.2 : 0.4, ease: [0.77, 0, 0.175, 1], delay: opening ? 0.7 : 0 }}
          >
            {/* Body */}
            <div className="absolute inset-0 shadow-[0_40px_80px_rgba(58,10,24,0.45)] ring-1 ring-gold/25 overflow-hidden"
              style={{ background: "linear-gradient(135deg, #FAF6EE 0%, #F1E3D6 100%)" }}>
              <div className="absolute inset-6 border border-rani/25" />
              {/* Ghost hearts: three nested gold outlines behind the seal */}
              <svg className="absolute inset-0 w-full h-full opacity-30" viewBox="0 0 400 260" fill="none" preserveAspectRatio="xMidYMid meet">
                <g stroke="#C9A961" strokeWidth="0.6">
                  {[150, 112, 74].map((k) => (
                    <path
                      key={k}
                      transform={`translate(200 128) scale(${k})`}
                      vectorEffect="non-scaling-stroke"
                      d="M0 0.6 C -0.9 0 -0.7 -0.75 0 -0.35 C 0.7 -0.75 0.9 0 0 0.6 Z"
                    />
                  ))}
                </g>
              </svg>
            </div>

            {/* Flap */}
            <motion.div
              className="absolute top-0 left-0 w-full h-[60%] origin-top z-[3]"
              style={{
                background: "linear-gradient(180deg, #F1E3D6 0%, #E4CFBE 100%)",
                clipPath: "polygon(0 0, 100% 0, 50% 100%)",
                boxShadow: "0 2px 0 rgba(201,169,97,0.35)",
              }}
              animate={{ rotateX: opening ? 180 : 0 }}
              transition={{ duration: 0.9, ease: [0.7, -0.05, 0.3, 1] }}
            />

            {/* Wax Seal */}
            <motion.div
              className="absolute top-1/2 left-1/2 w-[18%] aspect-square rounded-full flex items-center justify-center font-display italic text-[min(28px,5.5vw,6vh)] z-[5] text-gold-light"
              style={{
                background: "radial-gradient(circle at 30% 30%, #D83B6B 0%, #B8174A 50%, #7C0E31 100%)",
                boxShadow:
                  "0 6px 14px rgba(58,10,24,0.4)," +
                  "inset -6px -6px 10px rgba(58,10,24,0.3)," +
                  "inset 4px 4px 8px rgba(255,210,230,0.35)",
                translateX: "-50%",
                translateY: "-50%",
              }}
              animate={{
                scale: opening ? 0.6 : 1,
                rotate: opening ? 40 : 0,
                opacity: opening ? 0 : 1,
              }}
              transition={{ duration: 0.5, ease: [0.5, 0.1, 0.2, 1.4] }}
            >
              <img src="/brand/logo.png" alt="" className="w-[64%] h-auto pointer-events-none" draggable="false" />
              <span className="absolute inset-[9%] border border-dashed border-gold-light/50 rounded-full" />
            </motion.div>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: opening ? 0 : 1 }}
          transition={{ duration: 0.5 }}
          className="relative z-20 text-center"
          style={{ marginTop: "clamp(14px, 3vh, 28px)", pointerEvents: opening ? "none" : "auto" }}
        >
          <button
            onClick={handleOpen}
            className="border border-gold text-gold-light px-8 py-3.5 text-[11px] tracking-[0.34em] uppercase transition-all duration-500 ease-out bg-transparent hover:bg-gold hover:text-rani-deep hover:tracking-[0.42em]"
          >
            Open the Invitation
          </button>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------- helpers ---------- */
function CornerOrnament({ className = "", flipX = false, flipY = false }) {
  const scale = `scale(${flipX ? -1 : 1}, ${flipY ? -1 : 1})`;
  return (
    <svg
      className={`absolute w-16 h-16 md:w-24 md:h-24 pointer-events-none opacity-60 ${className}`}
      viewBox="0 0 120 120"
      fill="none"
      style={{ transform: scale }}
      aria-hidden
    >
      <g stroke="#C9A961" strokeWidth="0.8" opacity="0.7">
        <path d="M0 0 Q 40 0 40 40 Q 40 80 0 80" />
        <path d="M0 0 Q 60 0 60 60 Q 60 100 0 100" />
        <circle cx="20" cy="20" r="4" fill="#C9A961" opacity="0.5" />
        <circle cx="50" cy="30" r="2" fill="#C9A961" />
        <path d="M10 40 L 30 50 M20 20 L 40 30" />
      </g>
    </svg>
  );
}
